import { KLAYO_COLORS } from '@/themes';
import { BaseSelectMenuOption } from '@/types/components/selector.types';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SelectChangeEvent, Stack } from '@mui/material';
import { GridActionsCellItem, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { ChangeEvent, SyntheticEvent } from 'react';
import TickIcon from '../../resources/images/icons-check.svg';
import DeleteIcon from '../../resources/images/icons-delete-blue.svg';
import MoreVertIcon from '../../resources/images/icons-more-vertical.svg';
import WarningIcon from '../../resources/images/icons-warning.svg';
import type {
  EmployeeTableRow,
  TableColumnDef,
  TableProps,
} from '../../types/components/table.types';
import { getHeaderName } from '../../types/components/table.types';
import { highlightKeyword } from '../../utilities';
import { AttributeProgressBar } from '../AttributeProgressBar';
import { Chip } from '../Chip';
import { ComplianceBadge } from '../ComplienceBadge';
import { ProfilePicture } from '../ProfilePicture';
import { SelectMenu } from '../SelectMenu';
import { Tooltip } from '../Tooltip';
import { Table } from './Table';

/** Helper to get visible from breakpoint - converts null to undefined */
function getVisibleFromBreakpoint<T extends EmployeeTableRow = EmployeeTableRow>(
  c: TableColumnDef<T>
): string | undefined {
  return (
    (c.visibleFromBreakpoint ?? (typeof c.type === 'object' && c.type?.visibleFromBreakpoint)) ||
    undefined
  );
}

/** Helper to get visible to breakpoint - converts null to undefined */
function getVisibleToBreakpoint<T extends EmployeeTableRow = EmployeeTableRow>(
  c: TableColumnDef<T>
): string | undefined {
  return (
    (c.visibleToBreakpoint ?? (typeof c.type === 'object' && c.type?.visibleToBreakpoint)) ||
    undefined
  );
}

interface EmployeeTableOwnProps<
  T extends EmployeeTableRow = EmployeeTableRow,
> extends TableProps<T> {
  showProficiency?: boolean;
  onEmployeeSelect?: (event: SyntheticEvent, row: T) => void;
  onEmployeeAction?: (event: SyntheticEvent, row: T) => void;
  onRemove?: (event: SyntheticEvent, row: T) => void;
  onGroupLevelTypeChange?: (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<BaseSelectMenuOption>,
    row: T
  ) => void;
  onGroupNotificationTypesChange?: (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<BaseSelectMenuOption>,
    row: T
  ) => void;
}

export class EmployeeTable<T extends EmployeeTableRow = EmployeeTableRow> extends Table<
  T,
  EmployeeTableOwnProps<T>
> {
  static columns = {
    summary: {
      field: 'summary',
      label: '',
      visibleToBreakpoint: 'md',
    },
    profilePic: {
      field: 'profilePic',
      label: '',
    },
    fullName: {
      field: 'fullName',
      label: 'tableColumns.name',
    },
    jobTitle: {
      field: 'jobTitle',
      label: 'tableColumns.job',
    },
    fullNameJobTitle: {
      field: 'fullNameJobTitle',
      label: 'tableColumns.employee',
    },
    profilePicFullName: {
      field: 'profilePicFullName',
      label: 'tableColumns.employee',
    },
    profilePicFullNameJobTitle: {
      field: 'profilePicFullNameJobTitle',
      label: 'tableColumns.employee',
    },
    isCompliant: {
      field: 'isCompliant',
      label: 'tableColumns.compliant',
    },
    employeeAttributesCount: {
      field: 'employeeAttributesCount',
      label: 'tableColumns.competencies',
      visibleFromBreakpoint: 'md',
    },
    jobAttributesCount: {
      field: 'jobAttributesCount',
      label: 'tableColumns.jobCompetencies',
      visibleFromBreakpoint: 'md',
    },
    gapAttributesCount: {
      field: 'gapAttributesCount',
      label: 'tableColumns.gaps',
      visibleFromBreakpoint: 'md',
    },
    averageProficiency: {
      field: 'averageProficiency',
      label: 'tableColumns.averageProficiency',
      visibleFromBreakpoint: 'md',
    },
    status: {
      field: 'status',
      label: 'tableColumns.status',
    },
    accessLevel: {
      field: 'accessLevel',
      label: 'tableColumns.accessLevel',
    },
    actions: {
      field: 'actions',
      label: 'tableColumns.actions',
    },
    removeAction: {
      field: 'remove',
      label: '',
      tooltip: 'tableColumns.delete',
    },
    userDetails: {
      field: 'userDetails',
      label: 'tableColumns.details',
    },
    groupLevelType: {
      field: 'groupLevelType',
      label: 'tableColumns.permission',
    },
    groupNotificationType: {
      field: 'groupNotificationType',
      label: 'tableColumns.notifications',
    },
    enrollmentCount: {
      field: 'enrollmentCount',
      label: 'tableColumns.enrollments',
    },
    overdueCount: {
      field: 'overdueCount',
      label: 'tableColumns.overdue',
    },
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [EmployeeTable.columns.fullName] as unknown as TableColumnDef<EmployeeTableRow>[],
    noRowsMessage: 'No rows, yet',
    showProficiency: false,
    rowHasAction: undefined,
  };

  constructor(props: EmployeeTableOwnProps<T>) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: props.sortModel
        ? props.sortModel
        : [
            {
              field: 'fullName',
              sort: 'asc',
            },
          ],
    };
  }

  onRowClick(params: GridRowParams<T>, e: SyntheticEvent): void {
    const { onEmployeeSelect } = this.props;
    if (onEmployeeSelect) onEmployeeSelect(e, params.row);
  }

  onRemoveAction(e: SyntheticEvent, row: T): void {
    const { onRemove } = this.props;
    if (onRemove) {
      onRemove(e, row);
    }
  }

  getRowId(row: T): string {
    return this.tableId + '_' + row.employeeId;
  }

  initColumns(): void {
    const {
      showProficiency,
      columns,
      sortable,
      onEmployeeAction,
      onGroupLevelTypeChange,
      onGroupNotificationTypesChange,
      disabled,
      keyword,
    } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === EmployeeTable.columns.summary)
          this.columns.push({
            field: 'summary',
            headerName: c.label || c.type?.label || '',
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            filterable: false,
            width: '100%',
            flex: 1,
            numRows: 4,
            rowHeight: '124px',
            disableReorder: true,
            renderCell: (params) => (
              <div className='klayo-table_multilinerow'>
                <div className='klayo-employeetable_multilinerow_row1'>{params.row.fullName}</div>
                <div className='klayo-employeetable_multilinerow_row2'>{params.row.jobTitle}</div>
                <div className='klayo-employeetable_multilinerow_row3'>
                  <Stack
                    direction='row'
                    spacing={2}
                    justifyContent='space-between'
                    alignItems='baseline'
                  >
                    <div>
                      {this.props.translate?.('addEditGroup.competenciesGapsSummary', {
                        competencies: params.row.jobAttributesCount,
                        gaps: params.row.gapAttributesCount,
                      })}
                    </div>
                    <ComplianceBadge isCompliant={params.row.isCompliant} />
                  </Stack>
                </div>
                <div className='klayo-employeetable_multilinerow_row4'>
                  <AttributeProgressBar
                    label={this.props.translate?.('team.jobMatchOverview') ?? 'Job Match Overview'}
                    showLabel={false}
                    barHeight={8}
                    /*met={metAttributesCount} total={params.row.employeeAttributesCount}*/
                    met={params.row.numberOfMetMandatoryAndRequiredAttributes}
                    total={params.row.totalNumberOfMandatoryAndRequiredAttributesForJob}
                  />
                </div>
              </div>
            ),
          });

        if (c.type === EmployeeTable.columns.profilePic)
          this.columns.push({
            field: 'profilePic',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 70,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => (
              <ProfilePicture
                user={params.row}
                size='sm'
                onClick={(e: SyntheticEvent) => this.onUserClick(e, params.row)}
              />
            ),
          });

        if (c.type === EmployeeTable.columns.fullName)
          this.columns.push({
            field: 'fullName',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            flex: 1,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => highlightKeyword(params.row.fullName ?? '', keyword),
          });

        if (c.type === EmployeeTable.columns.jobTitle)
          this.columns.push({
            field: 'jobTitle',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            flex: 1,
            filterable: false,
            disableReorder: true,
          });

        if (c.type === EmployeeTable.columns.userDetails)
          this.columns.push({
            field: 'userDetails',
            sortable: sortable,
            headerName: '',
            align: 'right',
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 250,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) =>
              row.isTeamLead
                ? (this.props.translate?.('addEditGroup.teamLead') ?? 'Team lead')
                : '',
          });

        if (c.type === EmployeeTable.columns.fullNameJobTitle)
          this.columns.push({
            field: 'fullNameJobTitle',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            width: 250,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.fullName,
            renderCell: (params) => (
              <div>
                <div className='klayo_employeetable_name'>{params.row.fullName}</div>
                <div className='klayo_employeetable_job'>{params.row.jobTitle}</div>
              </div>
            ),
          });

        if (c.type === EmployeeTable.columns.profilePicFullName)
          this.columns.push({
            field: 'profilePicFullName',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            flex: 1,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.fullName,
            renderCell: (params) => (
              <div className='klayo_employeetable_pic klayo_employeetable_picname'>
                <ProfilePicture
                  user={params.row}
                  size='sm'
                  onClick={(e: SyntheticEvent) => this.onUserClick(e, params.row)}
                />
                <div>
                  <div className='klayo_employeetable_name'>
                    {highlightKeyword(params.row.fullName ?? '', keyword)}
                  </div>
                </div>
                {(c.showRole ?? true) && (
                  <>
                    {params.row.role && params.row.isOrganizationAdmin && (
                      <div className='klayo_employeetable_adminbadge'>
                        {this.props.translate?.('roles.admin') ?? 'Admin'}
                      </div>
                    )}
                    {params.row.role && params.row.isBilling && (
                      <div className='klayo_employeetable_billingbadge'>
                        {this.props.translate?.('roles.billing') ?? 'Billing'}
                      </div>
                    )}
                    {params.row.role && params.row.isInstructor && (
                      <div className='klayo_employeetable_instructorbadge'>
                        {this.props.translate?.('roles.instructor') ?? 'Instructor'}
                      </div>
                    )}
                    {params.row.role && params.row.isTrainingManager && (
                      <div className='klayo_employeetable_trainingmanagerbadge'>
                        {params.row.trainingManagerConfigurationTypeName}
                      </div>
                    )}
                  </>
                )}
              </div>
            ),
          });

        if (c.type === EmployeeTable.columns.profilePicFullNameJobTitle)
          this.columns.push({
            field: c.field || 'profilePicFullNameJobTitle',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            flex: 1,
            minWidth: 250,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.fullName,
            renderCell: (params) => (
              <div
                className='klayo_employeetable_pic klayo_employeetable_picjobname'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <ProfilePicture
                  user={params.row}
                  size='sm'
                  onClick={(e: SyntheticEvent) => this.onUserClick(e, params.row)}
                  style={{ flexShrink: 0 }}
                />
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Tooltip title={params.row.fullName} placement='bottom'>
                    <div
                      className='klayo_employeetable_name'
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {params.row.fullName}
                    </div>
                  </Tooltip>
                  <div className='klayo_employeetable_job truncate'>{params.row.jobTitle}</div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    flexShrink: 0,
                  }}
                >
                  {params.row.isOrganizationAdmin && (
                    <div className='klayo_employeetable_adminbadge'>
                      {this.props.translate?.('roles.admin') ?? 'Admin'}
                    </div>
                  )}
                  {params.row.role && params.row.isBilling && (
                    <div className='klayo_employeetable_billingbadge'>
                      {this.props.translate?.('roles.billing') ?? 'Billing'}
                    </div>
                  )}
                  {params.row && params.row.isInstructor && (
                    <div className='klayo_employeetable_instructorbadge'>
                      {this.props.translate?.('roles.instructor') ?? 'Instructor'}
                    </div>
                  )}
                  {params.row && params.row.isTrainingManager && (
                    <div
                      className='klayo_employeetable_trainingmanagerbadge'
                      style={{
                        marginLeft: 0,
                      }}
                    >
                      {params.row.trainingManagerConfigurationTypeName}
                    </div>
                  )}
                </div>
              </div>
            ),
          });

        if (c.type === EmployeeTable.columns.isCompliant)
          this.columns.push({
            field: 'isCompliant',
            sortable: c.sortable ?? sortable,
            headerName: c.visibleHeader ? getHeaderName(c, this.props.translate) : '',
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            align: c.align,
            headerAlign: c.headerAlign,
            width: c.width || 170,
            valueGetter: (_value, row) => row.isCompliant,
            renderCell: (params) =>
              !c.visibleIcon ? (
                params.row.isCompliant ? (
                  <div className='klayo_employeetable_compliantbadge'>
                    {this.props.translate?.('common.yes') ?? 'Yes'}
                  </div>
                ) : (
                  <div className='klayo_employeetable_noncompliantbadge'>
                    {this.props.translate?.('common.no') ?? 'No'}
                  </div>
                )
              ) : params.row.isCompliant ? (
                <TickIcon className='klayo-attrtable_checkicon' />
              ) : (
                <WarningIcon className='klayo-attrtable_warningicon' />
              ),
          });

        if (c.type === EmployeeTable.columns.employeeAttributesCount)
          this.columns.push({
            field: 'employeeAttributesCount',
            sortable: c.sortable ?? sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            width: 140,
            type: 'number',
          });

        if (c.type === EmployeeTable.columns.jobAttributesCount)
          this.columns.push({
            field: 'jobAttributesCount',
            sortable: c.sortable ?? sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            width: 140,
            type: 'number',
          });

        if (c.type === EmployeeTable.columns.status)
          this.columns.push({
            field: 'status',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) => row.statusLabel,
            renderCell: (params) => {
              const statusKey = params.row.statusLabel?.toLowerCase();
              if (statusKey && this.props.translate) {
                return this.props.translate(`status.${statusKey}`);
              }
              return params.row.statusLabel;
            },
            width: 170,
          });

        if (c.type === EmployeeTable.columns.accessLevel)
          this.columns.push({
            field: 'accessLevel',
            sortable: false,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            width: 140,
            valueGetter: (_value, row) =>
              row.isFullAccess
                ? (this.props.translate?.('accessLevel.full') ?? 'Full')
                : (this.props.translate?.('accessLevel.limited') ?? 'Limited'),
          });

        if (c.type === EmployeeTable.columns.gapAttributesCount)
          this.columns.push({
            field: 'gapAttributesCount',
            sortable: c.sortable ?? sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            width: c.width || 140,
            type: 'number',
          });

        if (c.type === EmployeeTable.columns.averageProficiency && showProficiency)
          this.columns.push({
            field: c.field,
            sortable: c.sortable ?? sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            filterable: false,
            disableReorder: true,
            type: 'number',
            width: 200,
            valueGetter: (_value, row) => EmployeeTable.calculateOverallProficiency(row),
            renderCell: (params) => {
              const metAttributesCount =
                (params.row.employeeAttributesCount ?? 0) - (params.row.gapAttributesCount ?? 0);
              return (
                <AttributeProgressBar
                  label='Job Match Overview'
                  showLabel={false}
                  barHeight={8}
                  /*met={metAttributesCount} total={params.row.employeeAttributesCount}*/
                  met={params.row.numberOfMetMandatoryAndRequiredAttributes}
                  total={params.row.totalNumberOfMandatoryAndRequiredAttributesForJob}
                />
              );
            },
            renderHeader: (params: { colDef: { headerName?: string } }) => (
              <Tooltip
                title={
                  this.props.translate?.('addEditGroup.percentageOfCompliance') ??
                  'Percentage of Compliance and Required competencies'
                }
                placement='top'
              >
                <div className='MuiDataGrid-columnHeaderTitle'>{params.colDef.headerName}</div>
              </Tooltip>
            ),
          });

        if (c.type === EmployeeTable.columns.actions)
          this.columns.push({
            field: 'actions',
            type: 'actions',
            filterable: false,
            disableReorder: true,
            align: 'right',
            getActions: (params) => [
              <GridActionsCellItem
                icon={<MoreVertIcon />}
                label={this.props.translate?.('tableColumns.actions') ?? 'Actions'}
                disabled={disabled}
                onClick={(e) => onEmployeeAction?.(e, params.row)}
                key={Math.random().toString(36).substring(7)}
              />,
            ],
          });

        if (c.type === EmployeeTable.columns.removeAction)
          this.columns.push({
            field: 'removeAction',
            type: 'actions',
            filterable: false,
            disableReorder: true,
            align: 'right',
            // A row the table may not remove (rowHasAction false) shows why instead: e.g. a group member the
            // organization's directory added.
            getActions: (params) =>
              typeof this.props.rowHasAction === 'function' && !this.props.rowHasAction(params.row)
                ? [
                    <Tooltip
                      key={Math.random().toString(36).substring(7)}
                      title={this.props.translate?.('employees.groupMemberSetInDirectory') ?? ''}
                      placement='left'
                    >
                      <LockOutlinedIcon
                        fontSize='small'
                        color='disabled'
                        aria-label={
                          this.props.translate?.('employees.groupMemberSetInDirectory') ?? ''
                        }
                      />
                    </Tooltip>,
                  ]
                : [
                    <GridActionsCellItem
                      label=''
                      icon={<DeleteIcon />}
                      disabled={disabled}
                      onClick={(e) => this.onRemoveAction(e, params.row)}
                      key={Math.random().toString(36).substring(7)}
                    />,
                  ],
          });

        if (c.type === EmployeeTable.columns.groupLevelType)
          this.columns.push({
            field: 'groupLevelType',
            headerName: getHeaderName(c, this.props.translate),
            filterable: false,
            disableReorder: true,
            width: 160,
            renderCell: (params) => (
              <SelectMenu<BaseSelectMenuOption>
                className='klayo-table_select'
                clearable={false}
                dense={true}
                noSelectionError={true}
                placeholder={this.props.translate?.('common.select') ?? 'Select'}
                value={c.groupLevelTypes?.find(
                  (t: { id: number | string }) => t.id === params.row.groupLevelType
                )}
                items={Object.entries(c.groupLevelTypes ?? {})}
                onChange={(e) => onGroupLevelTypeChange?.(e, params.row)}
                disabled={disabled}
                sx={{ width: '100%' }}
              />
            ),
          });

        if (c.type === EmployeeTable.columns.groupNotificationType)
          this.columns.push({
            field: 'groupNotificationType',
            headerName: getHeaderName(c, this.props.translate),
            filterable: false,
            disableReorder: true,
            width: 160,
            renderCell: (params) => (
              <SelectMenu<BaseSelectMenuOption>
                className='klayo-table_select'
                clearable={false}
                dense={true}
                noSelectionError={true}
                placeholder={this.props.translate?.('common.select') ?? 'Select'}
                value={c.groupNotificationTypes?.find(
                  (t: { id: number | string }) => t.id === params.row.groupNotificationType
                )}
                items={Object.entries(c.groupNotificationTypes ?? {})}
                onChange={(e) => onGroupNotificationTypesChange?.(e, params.row)}
                disabled={disabled}
                sx={{ width: '100%' }}
              />
            ),
          });

        if (c.type === EmployeeTable.columns.enrollmentCount)
          this.columns.push({
            field: 'enrollmentCount',
            headerName: getHeaderName(c, this.props.translate),
            filterable: false,
            disableReorder: true,
            width: 160,
            align: c.align,
            sortable: false,
            headerAlign: c.align,
            renderCell: (params) => (
              <Chip
                label={params.row.enrolments}
                textColor={KLAYO_COLORS.NeonBlueNB800}
                backgroundColor={KLAYO_COLORS.NeonBlueNB050}
              />
            ),
          });

        if (c.type === EmployeeTable.columns.overdueCount)
          this.columns.push({
            field: 'overdueCount',
            headerName: getHeaderName(c, this.props.translate),
            headerAlign: c.align,
            filterable: false,
            disableReorder: true,
            width: 160,
            align: c.align,
            sortable: false,
            renderCell: (params) => (
              <Chip
                label={params.row.overdue}
                textColor={KLAYO_COLORS.Red600}
                backgroundColor={KLAYO_COLORS.Rose050}
              />
            ),
          });
      }
    });
  }

  static calculateOverallProficiency(e: EmployeeTableRow): number | null {
    return AttributeProgressBar.calculatePercent(
      e.numberOfMetMandatoryAndRequiredAttributes || 0,
      e.totalNumberOfMandatoryAndRequiredAttributesForJob || 0
    );
  }

  static comparator(
    sortModel: GridSortModel,
    e1: EmployeeTableRow,
    e2: EmployeeTableRow
  ): number | undefined {
    if (!sortModel[0]) return;

    let sortCol = sortModel[0].field;
    const sortDir = sortModel[0].sort;

    if (sortCol === 'profilePicFullNameJobTitle' || sortCol === 'fullNameJobTitle')
      sortCol = 'fullName';

    if (sortCol === 'employeeAttributesCount') {
      return sortDir === 'asc'
        ? (e1.employeeAttributesCount ?? 0) - (e2.employeeAttributesCount ?? 0)
        : (e2.employeeAttributesCount ?? 0) - (e1.employeeAttributesCount ?? 0);
    } else if (sortCol === 'jobAttributesCount') {
      return sortDir === 'asc'
        ? (e1.jobAttributesCount ?? 0) - (e2.jobAttributesCount ?? 0)
        : (e2.jobAttributesCount ?? 0) - (e1.jobAttributesCount ?? 0);
    } else if (sortCol === 'gapAttributesCount') {
      return sortDir === 'asc'
        ? (e1.gapAttributesCount ?? 0) - (e2.gapAttributesCount ?? 0)
        : (e2.gapAttributesCount ?? 0) - (e1.gapAttributesCount ?? 0);
    } else if (sortCol === 'averageProficiency') {
      const e1Prof = EmployeeTable.calculateOverallProficiency(e1) ?? 0;
      const e2Prof = EmployeeTable.calculateOverallProficiency(e2) ?? 0;
      return sortDir === 'asc' ? e1Prof - e2Prof : e2Prof - e1Prof;
    } else {
      const e1Value = e1[sortCol as keyof EmployeeTableRow];
      const e2Value = e2[sortCol as keyof EmployeeTableRow];
      return sortDir === 'asc'
        ? String(e1Value ?? '').localeCompare(String(e2Value ?? ''))
        : String(e2Value ?? '').localeCompare(String(e1Value ?? ''));
    }
  }
}
